/**
 * QUIZ DATA PERSISTENCE UTILITY
 *
 * This utility handles saving and retrieving quiz answers from localStorage
 * so they persist across page refreshes and can be passed to GoHighLevel forms.
 *
 * Used in conjunction with UTM tracking to provide complete attribution data.
 */

import { QuizData } from './ValuationCalculator'

// localStorage key for quiz data
const QUIZ_DATA_KEY = 'chiro_quiz_data'

/**
 * Save quiz data to localStorage
 * Called after each step or field change to ensure data persists
 *
 * @param data - The quiz data object to save
 */
export function saveQuizData(data: Partial<QuizData>): void {
  try {
    // Get existing data
    const existing = getQuizData()

    // Merge with new data (new data takes priority)
    const merged = { ...existing, ...data }

    // Save to localStorage
    localStorage.setItem(QUIZ_DATA_KEY, JSON.stringify(merged))

    console.log('Quiz data saved:', merged)
  } catch (error) {
    console.error('Failed to save quiz data:', error)
  }
}

/**
 * Get quiz data from localStorage
 * Returns empty object if no data exists
 *
 * @returns Saved quiz data or empty object
 */
export function getQuizData(): Partial<QuizData> {
  try {
    const stored = localStorage.getItem(QUIZ_DATA_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch (error) {
    console.error('Failed to retrieve quiz data:', error)
    return {}
  }
}

/**
 * Clear quiz data from localStorage
 * Called on confirmation page after successful conversion
 */
export function clearQuizData(): void {
  try {
    localStorage.removeItem(QUIZ_DATA_KEY)
    console.log('Quiz data cleared')
  } catch (error) {
    console.error('Failed to clear quiz data:', error)
  }
}

/**
 * Convert quiz data to URL query parameters
 * Used to append quiz answers to GHL form iframe src
 *
 * @returns URLSearchParams object with all quiz data
 */
export function getQuizDataAsURLParams(): URLSearchParams {
  const data = getQuizData()
  const params = new URLSearchParams()

  // Add each quiz field as a parameter
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      // Handle arrays (like addBacks) by joining with commas
      if (Array.isArray(value)) {
        params.set(key, value.join(','))
      } else {
        params.set(key, String(value))
      }
    }
  })

  return params
}

/**
 * Check if quiz data exists in localStorage
 * Useful for determining if user has started the quiz
 *
 * @returns true if quiz data exists, false otherwise
 */
export function hasQuizData(): boolean {
  const data = getQuizData()
  return Object.keys(data).length > 0
}

/**
 * Get quiz completion percentage
 * Useful for analytics or progress tracking
 *
 * @returns Number between 0-100 representing completion percentage
 */
export function getQuizCompletionPercentage(): number {
  const data = getQuizData()

  // Required fields for a complete quiz
  const requiredFields = [
    'isOwner',
    'state',
    'revenue',
    'profit',
    'locations',
    'providers',
    'ownerHours',
    'concentration',
    'trend',
    'timeline',
  ]

  // Count how many required fields are filled
  const filledFields = requiredFields.filter((field) => {
    const value = data[field as keyof QuizData]
    return value !== undefined && value !== null && value !== ''
  })

  return Math.round((filledFields.length / requiredFields.length) * 100)
}
